/**
 * Upload multipart com progresso (`XMLHttpRequest`) compartilhado pelos
 * anexos de projeto e de entidade, com o mesmo mapeamento de erros
 * (413 → `AttachmentTooLargeError`, 400 → `AttachmentValidationError`).
 */
import type { AttachmentEntityType } from '@internal/orquestra-contract';
import type { AttachmentUploadProgress } from './OrquestraApi.types';
import {
  AttachmentTooLargeError,
  AttachmentValidationError,
  OrquestraRequestError,
} from './OrquestraErrors';

export interface UploadRequest {
  url: string;
  token?: string;
  file: File;
  onProgress?: (progress: AttachmentUploadProgress) => void;
  networkErrorMessage: string;
}

/** Lê `{ error }` do corpo do XHR (JSON opcional). */
function xhrErrorDetail(xhr: XMLHttpRequest): string {
  try {
    return (JSON.parse(xhr.responseText) as { error?: string })?.error ?? '';
  } catch {
    return '';
  }
}

/** Mapeia a resposta de erro do upload: 413, 400 ou erro genérico. */
function uploadError(xhr: XMLHttpRequest): OrquestraRequestError {
  const detail = xhrErrorDetail(xhr);
  if (xhr.status === 413) {
    return new AttachmentTooLargeError(
      detail || 'Arquivo excede o tamanho máximo permitido.',
    );
  }
  if (xhr.status === 400) {
    return new AttachmentValidationError(detail || 'Anexo inválido.');
  }
  return new OrquestraRequestError(
    detail || `Falha ao enviar anexo (${xhr.status}).`,
    xhr.status,
  );
}

/**
 * Upload multipart (campo `file`) via `XMLHttpRequest` para emitir progresso,
 * compartilhado pelos anexos de projeto e de entidade.
 */
export function uploadWithProgress<T>(request: UploadRequest): Promise<T> {
  const { url, token, file, onProgress } = request;
  const form = new FormData();
  form.append('file', file);
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    if (onProgress) {
      xhr.upload.onprogress = event => {
        onProgress({
          loaded: event.loaded,
          total: event.lengthComputable ? event.total : file.size,
        });
      };
    }
    xhr.onerror = () =>
      reject(new OrquestraRequestError(request.networkErrorMessage, 0));
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(uploadError(xhr));
        return;
      }
      try {
        resolve(JSON.parse(xhr.responseText) as T);
      } catch {
        reject(
          new OrquestraRequestError(
            'Resposta inválida do backend ao enviar anexo.',
            xhr.status,
          ),
        );
      }
    };
    xhr.send(form);
  });
}

/** Caminho base dos anexos de uma entidade do projeto. */
export function entityAttachmentsPath(
  projectId: number,
  entityType: AttachmentEntityType,
  entityId: number,
): string {
  return `/projects/${projectId}/attachments/${entityType}/${entityId}`;
}
